// useAuth — provider + hook do contexto de autenticação do builder.
//
// Rotina:
//   1. Boot: chama authApi.me() pra checar sessão existente. Mostra spinner.
//   2. Sem sessão → AuthScreen.
//   3. Login/signup: chama authApi.login/signup, atualiza state.
//   4. Logout: chama authApi.logout, limpa state, dispara navegação.
//   5. Listener no evento global 'nanoapp:unauthorized' (qualquer 401 vindo
//      de api.ts) zera user → AuthScreen.

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { authApi, type UserDTO } from '../lib/api';
import { hydrateAppSettingsFromServer } from '../utils/appSettings';

interface AuthContextValue {
  user: UserDTO | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserDTO | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authApi.me();
        if (cancelled) return;
        setUser(r?.user ?? null);
        // Hidrata preferências server-side (Sprint 8). Best-effort: erros
        // mantêm o cache local. Não bloqueia o boot.
        if (r?.user) hydrateAppSettingsFromServer().catch(() => {});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    function onUnauthorized() {
      setUser(null);
    }
    window.addEventListener('nanoapp:unauthorized', onUnauthorized);
    return () => {
      cancelled = true;
      window.removeEventListener('nanoapp:unauthorized', onUnauthorized);
    };
  }, []);

  async function login(email: string, password: string) {
    const r = await authApi.login(email, password);
    setUser(r.user);
    hydrateAppSettingsFromServer().catch(() => {});
  }

  async function signup(email: string, password: string) {
    const r = await authApi.signup(email, password);
    setUser(r.user);
    hydrateAppSettingsFromServer().catch(() => {});
  }

  async function logout() {
    try { await authApi.logout(); } catch { /* ignora — sessão pode já ter expirado */ }
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth precisa estar dentro de <AuthProvider>');
  return ctx;
}
